import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ─── Kill Radix UI's ResizeObserver overlay noise AT THE SOURCE ─────────
//
// Radix Select/Popover measures its floating trigger synchronously inside
// a ResizeObserver callback. When that measurement mutates layout, the
// browser fires "ResizeObserver loop completed with undelivered
// notifications" — harmless per spec (deferred to the next frame) but
// pops CRA's react-error-overlay every single time.
//
// window.error swallows can't win the race against CRA's overlay listener,
// so we monkey-patch ResizeObserver itself: every callback is deferred
// into requestAnimationFrame, which means the observer never fires
// synchronously within the same loop iteration and the "undelivered
// notifications" error simply never gets emitted.
//
// This must run BEFORE any Radix module imports its ResizeObserver
// reference — which happens the moment React mounts App — so we do it
// right here at the top of index.js.
(function patchResizeObserver() {
  if (typeof window === 'undefined' || !window.ResizeObserver) return;
  const OriginalRO = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalRO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || entries.length === 0) return;
          try { callback(entries, observer); } catch { /* swallow — dev-noise only */ }
        });
      });
    }
  };
})();

// Belt-and-braces: also swallow the message on the two error surfaces
// CRA's overlay listens to, in case something else (an older RO ref
// captured before our patch, a browser extension, etc.) still emits it.
(function silenceResidualRoErrors() {
  const RO_RE = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
  const _origError = window.console.error;
  window.console.error = (...args) => {
    const first = args?.[0];
    if (typeof first === 'string' && RO_RE.test(first)) return;
    if (first?.message && RO_RE.test(first.message)) return;
    _origError.apply(window.console, args);
  };
  const swallow = (e) => {
    const msg = e?.message || e?.reason?.message || String(e?.reason || '');
    if (RO_RE.test(msg)) {
      e.stopImmediatePropagation?.();
      e.preventDefault?.();
      return false;
    }
    return undefined;
  };
  window.addEventListener('error', swallow, true);
  window.addEventListener('unhandledrejection', swallow, true);
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
